import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Alert } from "@repo/ui";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux/store";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import { registrationSteps } from "../../constants/steps";
import withAuth from "../../../../hoc/withAuth/withAuth";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import { updateGiftTableState } from "../../../../redux/slices/giftTableSlice";
import { gsap } from "gsap";
import NavigationButtons from "../../components/NavigationButtons";

const LOCALE =
  "modules_Registration_pages_CreateGiftTable_CreateGiftTableStep4";

const CreateGiftTableStep4: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const giftTable = useSelector((state: RootState) => state.giftTable);

  const [messageToGuests, setMessageToGuests] = useState("");
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: "info" | "warn" | "error" | "success";
  } | null>(null);

  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (alertRef.current) {
      gsap.fromTo(
        alertRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 }
      );
    }
  }, [alert]);

  useEffect(() => {
    if (giftTable.messageToGuests) {
      setMessageToGuests(giftTable.messageToGuests);
    }
  }, [giftTable.messageToGuests]);

  const handleContinue = () => {
    if (!messageToGuests.trim()) {
      setAlert({ message: t("completeAllFieldsError"), level: "warn" });
      return;
    }

    dispatch(updateGiftTableState({ messageToGuests }));
    navigate("/registration/create-gift-table/choose-theme");
  };

  const handleBack = () => {
    navigate("/registration/create-gift-table/step3");
  };

  return (
    <PageContainer>
      <StepIndicator
        steps={registrationSteps}
        currentStep={4}
        progressPercentage={65}
      />
      <AnimatedCard title={t("title")} className="bg-white max-w-lg w-full">
        {alert && (
          <div ref={alertRef}>
            <Alert message={alert.message} level={alert.level} />
          </div>
        )}
        <div className="flex flex-col space-y-4">
          <label
            htmlFor="messageToGuests"
            className="text-label font-paragraph"
          >
            {t("messageToGuestsLabel")}
          </label>
          <textarea
            value={messageToGuests}
            onChange={(e) => setMessageToGuests(e.target.value)}
            className="input-border"
            rows={4}
          />
          <NavigationButtons
            continueLabel={t("continueButton")}
            backLabel={t("backButton", { defaultValue: "Regresar" })}
            onContinue={handleContinue}
            onBack={handleBack}
            className="w-full"
          />
        </div>
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(CreateGiftTableStep4));
